import {Haulier} from '@hconnect/apiclient'
import {Button, GenericInput} from '@hm/fundamental'
import {mdiMagnify} from '@mdi/js'
import {CircularProgress} from '@mui/material'
import {AxiosError} from 'axios'
import React, {useEffect, useState} from 'react'

import {useHaulierByNumber} from '../hooks/useHaulier'
import {useTranslation} from '../hooks/useTranslation'
import {GroupedRoleAssignment} from '../modules/ManageUsers.selectors'

type RoleHaulierLookupTextFieldProps = {
  hauliers: Haulier[]
  groupedRoleAssignment: GroupedRoleAssignment
  onChangeHaulierIds: (haulierIds: string[]) => void
  name: string
  dataScopeName: string
  helperText?: string
  disabled?: boolean
}

export const RoleHaulierLookupTextField = ({
  onChangeHaulierIds,
  helperText,
  dataScopeName,
  disabled,
  name
}: RoleHaulierLookupTextFieldProps) => {
  const {t} = useTranslation()

  const [textFieldValue, setTextFieldValue] = useState<string>('')

  const haulierQueryInfo = useHaulierByNumber(textFieldValue, false)

  const newHauliers = haulierQueryInfo.data
  const errorMessage = (haulierQueryInfo.error as AxiosError)?.message

  const noResultError =
    newHauliers?.length === 0 ? t(`roleAssignment.noLookupResult.${dataScopeName}`) : ''

  const handleLookup = async () => haulierQueryInfo.refetch()

  useEffect(() => {
    if (newHauliers && newHauliers.length > 0) {
      onChangeHaulierIds(newHauliers.map((haulier) => haulier.haulierId))
      haulierQueryInfo.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newHauliers])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    void handleLookup()
  }

  return (
    <div className="flex gap-2 w-full max-w-[600px]">
      <GenericInput
        label={t(`roleAssignment.dataScope.${dataScopeName}`)}
        value={disabled ? '' : textFieldValue}
        onChange={(value) => setTextFieldValue(value)}
        onKeyDown={handleKeyDown}
        error={errorMessage || helperText || noResultError}
        isDisabled={disabled}
        type="search"
        isTouched={true}
        data-cy="haulier-number-lookup"
        className="flex-1"
        name={name}
      />
      <Button
        data-cy="button-haulier-lookup"
        variant="secondary"
        icon={haulierQueryInfo.isInitialLoading ? <CircularProgress size={16} /> : mdiMagnify}
        type="button"
        className="h-[48px]"
        onClick={handleLookup}
        disabled={disabled}
      >
        Verify
      </Button>
    </div>
  )
}
